/**
 * Fase 3 — ranuras documentales por REQUISITO. Given the required document types
 * for an entity or a related person (`requiredDocsForEntity` / `requiredDocsForPerson`
 * in `@cumpli/shared`), it renders one labelled slot per required doc, marks which
 * are still missing, and lets the officer attach a file per slot.
 *
 * This is a NEW component on purpose: `DocUploadFlow` (a flat category-chip list) is
 * also consumed by `pages/ExpedienteDetail.tsx`, which is out of scope to touch, so
 * it stays intact. es-PA copy inline, matching the wizard's convention.
 */
import { useState, type DragEvent } from 'react';
import { Check, Paperclip, Camera, UploadCloud, FileQuestion, X } from 'lucide-react';
import {
  ALLOWED_DOCUMENT_MIME_TYPES,
  DOC_TYPE_LABELS,
  detectDocType,
  type DocTypeKey,
} from '@cumpli/shared';
import { FlowSelect } from './flow';
import { cn } from './ui';

const ACCEPT = ALLOWED_DOCUMENT_MIME_TYPES.join(',');

const isAllowed = (f: File) =>
  (ALLOWED_DOCUMENT_MIME_TYPES as readonly string[]).includes(f.type);

/** Un archivo soltado que la detección no pudo clasificar — espera asignación manual. */
interface PendingFile {
  id: string;
  file: File;
}

interface DocSlotsProps {
  slots: DocTypeKey[];
  /** Filename per slot when a file is staged; absent = still missing. */
  attached: Partial<Record<DocTypeKey, string>>;
  onAttach: (file: File, docType: DocTypeKey) => void;
  /** Shown when there are no required slots (e.g. beneficiario secundario). */
  emptyNote?: string;
}

/** One labelled slot: shows the requirement, a Falta/Adjuntado badge, and a picker.
 *  También acepta soltar un archivo directamente sobre la ranura (asignación directa). */
function Slot({
  docType,
  fileName,
  onAttach,
}: {
  docType: DocTypeKey;
  fileName: string | undefined;
  onAttach: (file: File, docType: DocTypeKey) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const done = fileName !== undefined;

  function pick(file: File) {
    setBusy(true);
    try {
      onAttach(file, docType);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files[0];
        if (file && isAllowed(file)) pick(file);
      }}
      className={cn(
        'rounded-2xl border-2 bg-panel p-4 transition-colors',
        over ? 'border-primary bg-primary/5' : done ? 'border-primary/40' : 'border-line-strong'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold leading-snug text-ink">{DOC_TYPE_LABELS[docType]}</p>
          {done && (
            <p className="mt-1 truncate font-mono text-[11px] text-ink-faint" title={fileName}>
              {fileName}
            </p>
          )}
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold',
            done ? 'bg-primary/12 text-primary-dark' : 'bg-tone-amber-bg text-tone-amber'
          )}
        >
          {done ? 'Adjuntado' : 'Falta'}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <label
          className={cn(
            'inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-line-strong bg-canvas px-3.5 py-2 text-sm font-bold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark',
            busy && 'pointer-events-none opacity-60'
          )}
        >
          <input
            type="file"
            accept={ACCEPT}
            className="sr-only"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) pick(file);
              e.target.value = '';
            }}
          />
          <Paperclip className="h-4 w-4" strokeWidth={1.75} />
          {done ? 'Reemplazar' : 'Adjuntar'}
        </label>
        <label
          className={cn(
            'inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-line-strong bg-canvas px-3.5 py-2 text-sm font-bold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark',
            busy && 'pointer-events-none opacity-60'
          )}
        >
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) pick(file);
              e.target.value = '';
            }}
          />
          <Camera className="h-4 w-4" strokeWidth={1.75} /> Tomar foto
        </label>
      </div>
    </div>
  );
}

/**
 * Renders the required slots; each row is independent so partial progress is clear.
 * Arriba, una zona de arrastrar-y-soltar multi-archivo: cada archivo soltado se
 * clasifica por nombre (`detectDocType`) y cae solo en su ranura; lo que no se
 * pudo clasificar queda en una cola "sin clasificar" para asignarlo a mano.
 */
export function DocSlots({ slots, attached, onAttach, emptyNote }: DocSlotsProps) {
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);

  if (slots.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-line bg-panel px-4 py-3 text-sm text-ink-soft">
        <Check className="h-4 w-4 text-primary-dark" strokeWidth={2} />
        {emptyNote ?? 'Sin requisitos documentales.'}
      </div>
    );
  }

  const doneCount = slots.filter((s) => attached[s] !== undefined).length;

  /** Clasifica y asigna cada archivo; lo no reconocido va a la cola manual. */
  function ingest(files: FileList | File[]) {
    const bad: string[] = [];
    const extras: PendingFile[] = [];
    // Ranuras ya ocupadas o llenadas durante ESTE lote — no se pisan solas.
    const taken = new Set<DocTypeKey>(slots.filter((s) => attached[s] !== undefined));
    for (const file of Array.from(files)) {
      if (!isAllowed(file)) {
        bad.push(file.name);
        continue;
      }
      const libre = slots.filter((s) => !taken.has(s));
      const dt = detectDocType(file.name, libre);
      if (dt) {
        taken.add(dt);
        onAttach(file, dt);
      } else {
        extras.push({ id: crypto.randomUUID(), file });
      }
    }
    setPending((prev) => [...prev, ...extras]);
    setRejected(bad);
  }

  function assignPending(p: PendingFile, dt: DocTypeKey) {
    onAttach(p.file, dt);
    setPending((prev) => prev.filter((x) => x.id !== p.id));
  }

  const slotOptions = slots.map((s) => ({ value: s, label: DOC_TYPE_LABELS[s] }));

  return (
    <div className="grid gap-3">
      {/* -------------------------------------------------- dropzone masiva */}
      <div
        onDragOver={(e: DragEvent) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e: DragEvent) => {
          e.preventDefault();
          setDragOver(false);
          ingest(e.dataTransfer.files);
        }}
        className={cn(
          'grid place-items-center gap-1 rounded-2xl border-2 border-dashed px-4 py-6 text-center transition-colors',
          dragOver ? 'border-primary bg-primary/5' : 'border-line-strong bg-canvas'
        )}
      >
        <UploadCloud className="h-6 w-6 text-ink-faint" strokeWidth={1.75} />
        <p className="text-sm font-bold text-ink">Arrastra aquí todos los documentos</p>
        <p className="text-sm text-ink-faint">
          Detectamos el tipo por el nombre del archivo y cada uno cae en su ranura.
        </p>
        <label className="mt-1 inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-line-strong bg-panel px-3.5 py-2 text-sm font-bold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark">
          <input
            type="file"
            accept={ACCEPT}
            multiple
            className="sr-only"
            onChange={(e) => {
              if (e.target.files) ingest(e.target.files);
              e.target.value = '';
            }}
          />
          <Paperclip className="h-4 w-4" strokeWidth={1.75} /> Elegir archivos
        </label>
        <p className="mt-1 text-xs text-ink-faint">
          {doneCount} de {slots.length} adjuntados
        </p>
      </div>

      {rejected.length > 0 && (
        <p className="rounded-xl border border-tone-amber/40 bg-tone-amber-bg px-4 py-3 text-sm text-tone-amber">
          Formato no admitido: {rejected.join(', ')}. Usa PDF, PNG, JPG, WebP o TIFF.
        </p>
      )}

      {/* -------------------------------------------- cola sin clasificar */}
      {pending.map((p) => (
        <div
          key={p.id}
          className="rounded-2xl border-2 border-dashed border-tone-amber/50 bg-panel p-4"
        >
          <div className="flex items-center gap-2">
            <FileQuestion className="h-5 w-5 shrink-0 text-tone-amber" strokeWidth={1.75} />
            <p className="min-w-0 flex-1 truncate text-sm font-bold text-ink" title={p.file.name}>
              {p.file.name}
            </p>
            <button
              type="button"
              aria-label={`Descartar ${p.file.name}`}
              onClick={() => setPending((prev) => prev.filter((x) => x.id !== p.id))}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-ink-faint transition-colors hover:bg-canvas hover:text-ink"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
          <p className="mt-1 text-sm text-ink-faint">
            No reconocimos este documento. Elige a qué requisito corresponde:
          </p>
          <div className="mt-2">
            <FlowSelect
              options={slotOptions}
              placeholder="Asignar a…"
              value=""
              onChange={(e) => {
                const dt = e.target.value as DocTypeKey;
                if (dt) assignPending(p, dt);
              }}
            />
          </div>
        </div>
      ))}

      {/* ------------------------------------------------ ranuras por requisito */}
      {slots.map((s) => (
        <Slot key={s} docType={s} fileName={attached[s]} onAttach={onAttach} />
      ))}
    </div>
  );
}
